import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bot, Container, Search, Send, Sparkles, X, Clock } from 'lucide-react';
import { api } from '../lib/api';
import { currentProduct } from '../lib/pageCatalog';
import { useDocStack, type DocType } from './DocStack';

interface SearchItem { type: string; id: string; title: string; subtitle: string; meta: string; score: number }
interface SearchGroupResp { type: string; label: string; items: SearchItem[] }
interface SearchResp { groups: SearchGroupResp[]; aiSummary: string | null; interpretedAs: string | null }
interface AssistantSource { type: string; id: string; title: string; deepLink: string }
interface ChatMsg { role: 'user' | 'assistant'; content: string; sources?: AssistantSource[] }

const RECENT_KEY = 'masterSearch.recent';
const MAX_RECENT = 6;
const SESSION_KEY = 'aiAssistant.sessionId';

function loadRecent(): string[] {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; }
}
function saveRecent(q: string) {
  try {
    const cur = loadRecent().filter((x) => x.toLowerCase() !== q.toLowerCase());
    cur.unshift(q);
    localStorage.setItem(RECENT_KEY, JSON.stringify(cur.slice(0, MAX_RECENT)));
  } catch { /* localStorage unavailable — recent search is a nicety, not required */ }
}
function getSessionId(): string {
  const fallback = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) { id = fallback(); sessionStorage.setItem(SESSION_KEY, id); }
    return id;
  } catch { return fallback(); }
}

// DocStack already owns detail modals for these entities app-wide — a search
// result or an AI-cited source just pushes onto the same stack everything
// else uses. Job orders/containers/bills/technicians instead deep-link via
// URL — those pages read the query param themselves (JobOrderTracker.tsx,
// PaymentRequestsTab.tsx) and clear it after opening the right drawer/modal.
const DOC_STACK_TYPES: Record<string, DocType> = {
  po: 'po', grn: 'grn', issue: 'issue', indent: 'indent',
  requisition: 'requisition', vendor: 'vendor', partcode: 'partcode', bill: 'bill' as DocType,
};

/**
 * Sidekick — a small anchored widget bottom-right (Ctrl/Cmd+K also opens it)
 * with two tabs: instant Search (every real entity via /api/search — see
 * backend/services/master-search.ts) and Ask, a real conversational
 * assistant (backend/services/ai-assistant.ts) that calls real tools —
 * search, and direct lookups on job orders/POs/GRNs/requisitions/vendors/
 * bills/issue notes — permission-filtered per user before the model ever
 * sees a tool exists. Both tabs deep-link to the exact record, never just
 * the module's list page.
 */
export function MasterSearch() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'search' | 'ask'>('search');
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const chatInputRef = useRef<HTMLTextAreaElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const docStack = useDocStack();
  const navigate = useNavigate();
  const location = useLocation();
  const sessionIdRef = useRef<string>(getSessionId());

  const [chatMessages, setChatMessages] = useState<ChatMsg[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 200);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQ(''); setDebounced(''); setActiveIdx(0);
      // Focus after the portal actually mounts.
      setTimeout(() => (tab === 'ask' ? chatInputRef.current : inputRef.current)?.focus(), 30);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ block: 'end' });
  }, [chatMessages, chatLoading]);

  // Restore any earlier conversation in this browser tab the first time the
  // Ask tab is opened (chat state otherwise lives only in this component,
  // which itself stays mounted for the whole session at the Layout level —
  // this call only matters right after a full page refresh).
  useEffect(() => {
    if (tab !== 'ask' || historyLoaded) return;
    setHistoryLoaded(true);
    api.get<{ turns: Array<{ userMessage: string; assistantAnswer: string; sources: AssistantSource[] }> }>(
      `/api/ai-assistant/history?sessionId=${encodeURIComponent(sessionIdRef.current)}`,
    ).then((res) => {
      if (!res.turns?.length) return;
      setChatMessages((cur) => {
        if (cur.length) return cur;
        return res.turns.flatMap((t) => [
          { role: 'user' as const, content: t.userMessage },
          { role: 'assistant' as const, content: t.assistantAnswer, sources: t.sources },
        ]);
      });
    }).catch(() => { /* history is a nicety — a fresh chat is a fine fallback */ });
  }, [tab, historyLoaded]);

  const { data, isLoading } = useQuery<SearchResp>({
    queryKey: ['master-search', debounced],
    // `product` tells the backend which Spares/Service/IMS subdomain this is
    // — Spares gets its own Job Orders/Containers narrowed to Stock-category
    // only, so a Spares user searching a number doesn't see Service's
    // complaint work orders mixed in (see master-search.ts's realm filter).
    queryFn: () => api.get(`/api/search?q=${encodeURIComponent(debounced)}&product=${encodeURIComponent(currentProduct() || '')}`),
    enabled: open && tab === 'search' && debounced.length >= 2,
    staleTime: 10_000,
  });

  const flatItems = useMemo(() => (data?.groups || []).flatMap((g) => g.items), [data]);
  const recent = useMemo(() => (open && debounced.length < 2 ? loadRecent() : []), [open, debounced]);

  /** The one place that decides where a result — from Search or from an AI
   *  answer's cited sources — actually opens. Job orders/containers/bills/
   *  technicians are URL deep links the target page itself understands;
   *  everything else pushes onto the shared DocStack. */
  const openResult = (r: { type: string; id: string }) => {
    setOpen(false);
    const docType = DOC_STACK_TYPES[r.type];
    if (docType) { docStack.open({ type: docType, id: r.id }); return; }
    if (r.type === 'jo') { navigate(`/jo-tracker?jo=${encodeURIComponent(r.id)}`); return; }
    if (r.type === 'container') { navigate(`/jo-tracker?container=${encodeURIComponent(r.id)}`); return; }
    if (r.type === 'bill') { navigate(`/bills?grn=${encodeURIComponent(r.id)}`); return; }
    if (r.type === 'technician') { navigate('/technicians'); return; }
  };

  const openItem = (item: SearchItem) => {
    saveRecent(q.trim() || item.title);
    openResult(item);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(flatItems.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => Math.max(0, i - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); const it = flatItems[activeIdx]; if (it) openItem(it); }
  };

  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text || chatLoading) return;
    setChatInput('');
    const priorHistory = chatMessages.map((m) => ({ role: m.role, content: m.content })).slice(-10);
    setChatMessages((h) => [...h, { role: 'user', content: text }]);
    setChatLoading(true);
    try {
      const res = await api.post<{ answer: string; sources: AssistantSource[] }>('/api/ai-assistant/chat', {
        sessionId: sessionIdRef.current,
        message: text,
        route: location.pathname,
        product: currentProduct() || '',
        history: priorHistory,
      });
      setChatMessages((h) => [...h, { role: 'assistant', content: res.answer, sources: res.sources || [] }]);
    } catch (e: any) {
      setChatMessages((h) => [...h, { role: 'assistant', content: `Something went wrong reaching the assistant: ${e?.message || 'please try again'}.` }]);
    } finally {
      setChatLoading(false);
    }
  };

  const onChatKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(); }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-5 right-5 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-brand-600 text-white shadow-lg shadow-brand-600/30 hover:bg-brand-700 hover:scale-105 transition-all"
        title="Sidekick — search everything, or ask a question (Ctrl/Cmd+K)"
        aria-label="Sidekick"
      >
        <Container size={22} />
      </button>

      {open && createPortal(
        <>
          {/* Transparent click-outside catcher — no dimming, this is an
              anchored widget, not a page-blocking dialog. */}
          <div className="fixed inset-0 z-[199]" onClick={() => setOpen(false)} />
          <div
            className="fixed bottom-[76px] right-5 z-[200] w-[400px] max-w-[92vw] max-h-[70dvh] rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-1 border-b border-slate-200 px-3 pt-2.5">
              <button
                type="button"
                onClick={() => setTab('search')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-t-lg ${tab === 'search' ? 'bg-slate-100 text-slate-800' : 'text-slate-400 hover:text-slate-600'}`}
              >
                <Search size={13} /> Search
              </button>
              <button
                type="button"
                onClick={() => setTab('ask')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-t-lg ${tab === 'ask' ? 'bg-slate-100 text-slate-800' : 'text-slate-400 hover:text-slate-600'}`}
              >
                <Bot size={13} /> Ask Sidekick
              </button>
              <button type="button" onClick={() => setOpen(false)} className="ml-auto mb-1.5 text-slate-400 hover:text-slate-700 shrink-0">
                <X size={16} />
              </button>
            </div>

            {tab === 'search' && (
              <>
                <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
                  <Search size={16} className="text-slate-400 shrink-0" />
                  <input
                    ref={inputRef}
                    value={q}
                    onChange={(e) => { setQ(e.target.value); setActiveIdx(0); }}
                    onKeyDown={onKeyDown}
                    placeholder="Search job orders, POs, GRNs, vendors, part codes, bills…"
                    className="flex-1 outline-none text-sm min-w-0"
                  />
                </div>

                <div className="overflow-y-auto flex-1">
                  {debounced.length < 2 && (
                    <div className="p-4">
                      {recent.length > 0 ? (
                        <>
                          <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-2">Recent searches</div>
                          <div className="flex flex-wrap gap-1.5">
                            {recent.map((r) => (
                              <button key={r} type="button" onClick={() => setQ(r)}
                                className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-600 hover:bg-slate-100">
                                <Clock size={11} /> {r}
                              </button>
                            ))}
                          </div>
                        </>
                      ) : (
                        <div className="text-sm text-slate-400 py-6 text-center">Type at least 2 characters to search everything.</div>
                      )}
                    </div>
                  )}

                  {debounced.length >= 2 && isLoading && (
                    <div className="p-6 text-sm text-slate-400 text-center">Searching…</div>
                  )}

                  {debounced.length >= 2 && !isLoading && data && flatItems.length === 0 && (
                    <div className="p-6 text-sm text-slate-400 text-center">No matches for "{debounced}".</div>
                  )}

                  {debounced.length >= 2 && !isLoading && data && data.interpretedAs && (
                    <div className="px-4 pt-2 text-[11px] text-slate-400">Searching for: <span className="font-medium text-slate-600">{data.interpretedAs}</span></div>
                  )}

                  {debounced.length >= 2 && !isLoading && data && data.aiSummary && (
                    <div className="mx-4 mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-slate-700 flex items-start gap-2">
                      <Sparkles size={13} className="text-amber-600 mt-0.5 shrink-0" />
                      <span>{data.aiSummary}</span>
                    </div>
                  )}

                  {debounced.length >= 2 && !isLoading && data && data.groups.map((g) => (
                    <div key={g.type} className="px-2 py-2">
                      <div className="px-2 text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-1">{g.label}</div>
                      {g.items.map((it) => {
                        const flatIdx = flatItems.indexOf(it);
                        const active = flatIdx === activeIdx;
                        return (
                          <button
                            key={`${it.type}-${it.id}`}
                            type="button"
                            onMouseEnter={() => setActiveIdx(flatIdx)}
                            onClick={() => openItem(it)}
                            className={`w-full text-left px-3 py-2 rounded-lg flex items-center justify-between gap-3 ${active ? 'bg-brand-50' : 'hover:bg-slate-50'}`}
                          >
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-slate-800 truncate">{it.title}</div>
                              {it.subtitle && <div className="text-xs text-slate-500 truncate">{it.subtitle}</div>}
                            </div>
                            {it.meta && <div className="text-[11px] text-slate-400 shrink-0 text-right max-w-[45%] truncate">{it.meta}</div>}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>

                <div className="border-t border-slate-100 px-4 py-1.5 flex items-center gap-3 text-[10px] text-slate-400">
                  <span>↑↓ navigate</span><span>Enter open</span><span>Esc close</span>
                  <span className="ml-auto">Ctrl/Cmd+K</span>
                </div>
              </>
            )}

            {tab === 'ask' && (
              <>
                <div className="overflow-y-auto flex-1 px-3 py-3 space-y-3 min-h-[280px]">
                  {chatMessages.length === 0 && (
                    <div className="text-xs text-slate-400 px-1 py-4 text-center">
                      Ask about any job order, PO, GRN, requisition, vendor, or bill — Sidekick looks up the real record, it never guesses.
                    </div>
                  )}
                  {chatMessages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${m.role === 'user' ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-800'}`}>
                        <div className="whitespace-pre-wrap">{m.content}</div>
                        {m.sources && m.sources.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {m.sources.map((s, si) => (
                              <button
                                key={si}
                                type="button"
                                onClick={() => openResult(s)}
                                className="inline-flex items-center rounded-full border border-slate-300 bg-white px-2 py-0.5 text-[11px] text-slate-600 hover:bg-slate-50 hover:border-brand-300"
                              >
                                {s.title}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {chatLoading && (
                    <div className="flex justify-start">
                      <div className="rounded-xl px-3 py-2 text-sm bg-slate-100 text-slate-400">Thinking…</div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
                <div className="border-t border-slate-200 p-2 flex items-end gap-2">
                  <textarea
                    ref={chatInputRef}
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={onChatKeyDown}
                    placeholder="Ask a question…"
                    rows={1}
                    className="flex-1 resize-none outline-none text-sm border border-slate-200 rounded-lg px-2.5 py-2 max-h-24"
                  />
                  <button
                    type="button"
                    onClick={sendChat}
                    disabled={chatLoading || !chatInput.trim()}
                    className="flex items-center justify-center h-9 w-9 rounded-lg bg-brand-600 text-white disabled:opacity-40 hover:bg-brand-700 shrink-0"
                    aria-label="Send"
                  >
                    <Send size={15} />
                  </button>
                </div>
              </>
            )}
          </div>
        </>,
        document.body,
      )}
    </>
  );
}
